import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Tags, Trash2, ShieldCheck, ChevronUp, ChevronDown, ArrowUpToLine, ArrowDownToLine, Save } from "lucide-react";

/**
 * AdminCategoryManager
 * Adds/removes custom business categories AND lets the admin reorder the
 * full category list — base + custom — with up/down arrows. The saved
 * order is respected everywhere /api/categories is consumed (signup,
 * admin filters, directory).
 */
export default function AdminCategoryManager() {
  const [base, setBase] = useState([]);
  const [custom, setCustom] = useState([]);
  // Full merged & ordered list (base + custom, minus "Other")
  const [ordered, setOrdered] = useState([]);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const customSet = new Set(custom.map((c) => c.name));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [customResp, catsResp] = await Promise.all([
        axios.get(`${API}/admin/categories/custom`),
        axios.get(`${API}/categories`),
      ]);
      setBase(customResp.data.base || []);
      setCustom(customResp.data.custom || []);
      // /api/categories returns the fully-ordered merged list — trust it
      const list = (catsResp.data.categories || []).filter((c) => c !== "Other");
      setOrdered(list);
      setOrderDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Enter a category name");
    setCreating(true);
    try {
      await axios.post(
        `${API}/admin/categories/custom`,
        { name }
      );
      toast.success(`Added "${name}"`);
      setNewName("");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add category");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (name) => {
    if (!window.confirm(`Remove custom category "${name}"? Businesses already using this label will keep their category value.`)) return;
    try {
      await axios.delete(
        `${API}/admin/categories/custom/${encodeURIComponent(name)}`
      );
      toast.success(`Removed "${name}"`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const moveTo = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= ordered.length || fromIdx === toIdx) return;
    setOrdered((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
    setOrderDirty(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await axios.put(
        `${API}/admin/categories/order`,
        { order: ordered }
      );
      toast.success("Category order saved — site updates on next page load");
      setOrderDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save order");
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-5" data-testid="admin-category-manager">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
            <Tags className="w-5 h-5" /> Business Categories
          </h2>
          <p className="text-sm text-gray-500 font-body">
            Add your own, remove custom ones, and drag with the arrows to
            reorder the whole list. Changes go live immediately after save.
          </p>
        </div>
        {orderDirty && (
          <Button
            onClick={saveOrder}
            disabled={savingOrder}
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
            data-testid="admin-category-save-order"
          >
            {savingOrder ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save order
          </Button>
        )}
      </div>

      {/* Add row */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          placeholder="e.g. Banking, Legal Services, Barber Shop…"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !creating && add()}
          maxLength={60}
          data-testid="admin-category-new-name"
        />
        <Button
          onClick={add}
          disabled={creating || !newName.trim()}
          className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
          data-testid="admin-category-add"
        >
          {creating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Plus className="w-4 h-4 mr-2" />
          )}
          Add Category
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-2">
            Category order ({ordered.length}) — Other is always last
          </h3>
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
            {ordered.map((name, i) => {
              const isCustom = customSet.has(name);
              return (
                <li
                  key={name}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50"
                  data-testid={`admin-category-row-${name}`}
                >
                  <span className="text-xs text-gray-400 font-mono w-6 flex-shrink-0">
                    #{i + 1}
                  </span>
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="text-sm text-tbc-navy truncate">{name}</span>
                    {isCustom ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-tbc-gold/15 text-tbc-navy border border-tbc-gold/40">
                        custom
                      </span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 flex items-center gap-0.5">
                        <ShieldCheck className="w-2.5 h-2.5" /> built-in
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={() => moveTo(i, 0)}
                      disabled={i === 0}
                      className="p-1 text-gray-500 hover:text-tbc-navy hover:bg-tbc-navy/5 rounded disabled:opacity-30"
                      title="Move to top"
                      data-testid={`admin-category-top-${name}`}
                    >
                      <ArrowUpToLine className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => moveTo(i, i - 1)}
                      disabled={i === 0}
                      className="p-1 text-gray-500 hover:text-tbc-navy hover:bg-tbc-navy/5 rounded disabled:opacity-30"
                      title="Move up"
                      data-testid={`admin-category-up-${name}`}
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => moveTo(i, i + 1)}
                      disabled={i === ordered.length - 1}
                      className="p-1 text-gray-500 hover:text-tbc-navy hover:bg-tbc-navy/5 rounded disabled:opacity-30"
                      title="Move down"
                      data-testid={`admin-category-down-${name}`}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => moveTo(i, ordered.length - 1)}
                      disabled={i === ordered.length - 1}
                      className="p-1 text-gray-500 hover:text-tbc-navy hover:bg-tbc-navy/5 rounded disabled:opacity-30"
                      title="Move to bottom"
                      data-testid={`admin-category-bottom-${name}`}
                    >
                      <ArrowDownToLine className="w-3.5 h-3.5" />
                    </button>
                    {isCustom && (
                      <button
                        onClick={() => remove(name)}
                        className="p-1 text-gray-400 hover:text-red-500 rounded ml-1"
                        title="Delete custom category"
                        data-testid={`admin-category-delete-${name}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-gray-400 mt-2 font-body">
            Built-in categories can&apos;t be removed. Custom ones can be deleted anytime.
          </p>
        </div>
      )}
    </div>
  );
}
